import React, { useState, useEffect, useRef } from "react";
import Modal from "../Modal/Modal";
import CustomSelect, { CustomSelectRef } from "../CoreComponents/CustomSelect";
import {
  Truck,
  TruckStatus,
  TrailerType,
  ContainerType,
  CreateTruckRequest,
  UpdateTruckRequest,
} from "../../interfaces/truck.interface";
import TruckService from "../../services/Truck.service";
import { useLoader } from "../../contexts/LoaderContext";
import { showToast } from "../Toaster";
import { IUser } from "../../interfaces/user.interface";
import TransporterService from "../../services/TransporterService";
import rbacUtils from "../../utils/rbacUtils";

interface TruckFormProps {
  isOpen: boolean;
  onClose: () => void;
  truck?: Truck | null;
  onSuccess: () => void;
  userProfile?: IUser | null;
}

const TruckForm: React.FC<TruckFormProps> = ({
  isOpen,
  onClose,
  truck,
  onSuccess,
  userProfile,
}) => {
  const { showLoader, hideLoader } = useLoader();

  const [formData, setFormData] = useState<CreateTruckRequest>({
    registrationNumber: "",
    transporterId: undefined,
    model: "",
    make: "",
    year: new Date().getFullYear(),
    capacity: 0,
    capacityUnit: "kg",
    fuelType: "Diesel",
    insuranceExpiryDate: Date.now(),
    permitExpiryDate: Date.now(),
    fitnessExpiryDate: Date.now(),
    pucExpiryDate: Date.now(),
    trailerType: TrailerType?.DOUBLE_TWENTY || "DOUBLE_TWENTY",
    containerType: ContainerType?.GP || "GP",
    status: TruckStatus?.ACTIVE || "ACTIVE",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dropdown options
  const [transporterOptions, setTransporterOptions] = useState<any[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Ref for CustomSelect
  const transporterSelectRef = useRef<CustomSelectRef>(null);

  const isEditMode = !!truck;

  useEffect(() => {
    if (truck) {
      // Edit mode - populate form with existing truck data
      setFormData({
        registrationNumber: truck.registrationNumber || "",
        transporterId: truck.transporterId,
        model: truck.model || "",
        make: truck.make || "",
        year: truck.year || new Date().getFullYear(),
        capacity: truck.capacity || 0,
        capacityUnit: truck.capacityUnit || "kg",
        fuelType: truck.fuelType || "Diesel",
        insuranceExpiryDate: truck.insuranceExpiryDate || Date.now(),
        permitExpiryDate: truck.permitExpiryDate || Date.now(),
        fitnessExpiryDate: truck.fitnessExpiryDate || Date.now(),
        pucExpiryDate: truck.pucExpiryDate || Date.now(),
        trailerType:
          truck.trailerType || TrailerType?.DOUBLE_TWENTY || "DOUBLE_TWENTY",
        containerType: truck.containerType || ContainerType?.GP || "GP",
        status: truck.status || TruckStatus?.ACTIVE || "ACTIVE",
      });
    } else {
      // Add mode - reset form
      setFormData({
        registrationNumber: "",
        transporterId: undefined,
        model: "",
        make: "",
        year: new Date().getFullYear(),
        capacity: 0,
        capacityUnit: "kg",
        fuelType: "Diesel",
        insuranceExpiryDate: Date.now(),
        permitExpiryDate: Date.now(),
        fitnessExpiryDate: Date.now(),
        pucExpiryDate: Date.now(),
        trailerType: TrailerType?.DOUBLE_TWENTY || "DOUBLE_TWENTY",
        containerType: ContainerType?.GP || "GP",
        status: TruckStatus?.ACTIVE || "ACTIVE",
      });
    }
    setErrors({});
  }, [truck]);

  // When transporterOptions or formData.transporterId changes, update CustomSelect value
  useEffect(() => {
    if (
      transporterSelectRef.current &&
      transporterOptions.length > 0 &&
      formData.transporterId !== null &&
      formData.transporterId !== undefined
    ) {
      transporterSelectRef.current.setSeletedOption(
        String(formData.transporterId)
      );
    }
  }, [transporterOptions, formData.transporterId]);

  // Fetch transporter options on mount
  useEffect(() => {
    async function fetchOptions() {
      if (!userProfile) {
        setTransporterOptions([]);
        return;
      }

      setLoadingOptions(true);
      try {
        const isTransporterAdmin = rbacUtils.isTransporterAdmin(userProfile);

        if (isTransporterAdmin) {
          // ROLE_TRANSPORTER_ADMIN: Show only self transporter
          const selfTransporter = {
            id: userProfile.org.id,
            name: userProfile.org.name,
          };
          setTransporterOptions([selfTransporter]);

          // Auto-select self transporter if not in edit mode
          if (!truck) {
            setFormData((prev) => ({
              ...prev,
              transporterId: selfTransporter.id,
            }));
          }
        } else {
          // Other roles: Fetch all transporters
          const transporterRes = await TransporterService.getTransporters(
            userProfile
          );
          let transporters = [];
          if (Array.isArray(transporterRes?.data?.data)) {
            transporters = transporterRes.data.data;
          } else if (transporterRes?.data?.data?.content) {
            transporters = transporterRes.data.data.content;
          } else if (Array.isArray(transporterRes?.data)) {
            transporters = transporterRes.data;
          }
          setTransporterOptions(transporters);
        }
      } catch (err) {
        console.error("Error fetching transporters:", err);
        setTransporterOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    }
    fetchOptions();
  }, [truck, userProfile]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.registrationNumber.trim()) {
      newErrors.registrationNumber = "Registration number is required";
    }

    if (!formData.transporterId) {
      newErrors.transporterId = "Transporter is required";
    }

    if (!formData.model.trim()) {
      newErrors.model = "Model is required";
    }

    if (!formData.make.trim()) {
      newErrors.make = "Make is required";
    }

    if (formData.year < 1900 || formData.year > new Date().getFullYear() + 1) {
      newErrors.year = "Invalid year";
    }

    if (formData.capacity <= 0) {
      newErrors.capacity = "Capacity must be greater than 0";
    }

    if (!formData.capacityUnit) {
      newErrors.capacityUnit = "Capacity unit is required";
    }

    if (!formData.fuelType.trim()) {
      newErrors.fuelType = "Fuel type is required";
    }

    // Validate expiry dates
    const now = Date.now();
    if (formData.insuranceExpiryDate < now) {
      newErrors.insuranceExpiryDate =
        "Insurance expiry date cannot be in the past";
    }

    if (formData.permitExpiryDate < now) {
      newErrors.permitExpiryDate = "Permit expiry date cannot be in the past";
    }

    if (formData.fitnessExpiryDate < now) {
      newErrors.fitnessExpiryDate = "Fitness expiry date cannot be in the past";
    }

    if (formData.pucExpiryDate < now) {
      newErrors.pucExpiryDate = "PUC expiry date cannot be in the past";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user selects an option
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleDateChange = (name: string, value: string) => {
    const timestamp = new Date(value).getTime();
    setFormData((prev) => ({
      ...prev,
      [name]: timestamp,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle CustomSelect change for transporter
  const handleTransporterChange = (selected: string | string[]) => {
    const value = Array.isArray(selected) ? selected[0] : selected;
    setFormData((prev) => ({
      ...prev,
      transporterId: value ? Number(value) : undefined,
    }));

    if (errors.transporterId) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.transporterId;
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    try {
      setIsSubmitting(true);
      showLoader();

      if (isEditMode) {
        // Update existing truck
        const updateData: UpdateTruckRequest = {
          ...formData,
          id: truck!.id!,
        };
        await TruckService.updateTruck(updateData);
        showToast("Truck updated successfully", "success");
      } else {
        // Create new truck
        await TruckService.createTruck(formData);
        showToast("Truck created successfully", "success");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error saving truck:", error);
      showToast(
        error.response?.data?.message || "Failed to save truck",
        "error"
      );
    } finally {
      setIsSubmitting(false);
      hideLoader();
    }
  };

  const formatDateForInput = (timestamp: number): string => {
    return new Date(timestamp).toISOString().split("T")[0];
  };

  const getFooterButtons = () => (
    <div className="flex justify-center gap-4">
      <button
        type="button"
        className="secondary_button w-[128px]"
        onClick={onClose}
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="primary_button w-[128px]"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Create"}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Truck" : "Add Truck"}
      size="xl"
      closeOnModalOverlayClick={true}
      footer={getFooterButtons()}
    >
      <div className="modal-form">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Registration Number */}
          <div className="form-group lg:col-span-2">
            <label className="form-label required_field_label">
              Registration Number
            </label>
            <input
              type="text"
              name="registrationNumber"
              value={formData.registrationNumber}
              onChange={handleInputChange}
              placeholder="Enter registration number"
              className={`form-input ${
                errors.registrationNumber ? "error" : ""
              }`}
            />
            {errors.registrationNumber && (
              <div className="form-help error">{errors.registrationNumber}</div>
            )}
          </div>

          {/* Transporter */}
          <div className="form-group ">
            <label className="form-label required_field_label">
              Transporter
            </label>
            <CustomSelect
              ref={transporterSelectRef}
              label="Transporter"
              options={transporterOptions.map((t) => ({
                value: String(t.id),
                label: t.name,
              }))}
              placeholder="Select transporter"
              onChange={handleTransporterChange}
              className="form-input"
              disabled={loadingOptions}
              error={!!errors.transporterId}
            />
            {errors.transporterId && (
              <div className="form-help error">{errors.transporterId}</div>
            )}
          </div>

          {/* Make */}
          <div className="form-group">
            <label className="form-label required_field_label">Make</label>
            <input
              type="text"
              name="make"
              value={formData.make}
              onChange={handleInputChange}
              placeholder="Enter make"
              className={`form-input ${errors.make ? "error" : ""}`}
            />
            {errors.make && (
              <div className="form-help error">{errors.make}</div>
            )}
          </div>

          {/* Model */}
          <div className="form-group">
            <label className="form-label required_field_label">Model</label>
            <input
              type="text"
              name="model"
              value={formData.model}
              onChange={handleInputChange}
              placeholder="Enter model"
              className={`form-input ${errors.model ? "error" : ""}`}
            />
            {errors.model && (
              <div className="form-help error">{errors.model}</div>
            )}
          </div>

          {/* Year */}
          <div className="form-group">
            <label className="form-label required_field_label">Year</label>
            <input
              type="number"
              name="year"
              value={formData.year}
              onChange={handleInputChange}
              min="1900"
              max={new Date().getFullYear() + 1}
              className={`form-input ${errors.year ? "error" : ""}`}
            />
            {errors.year && (
              <div className="form-help error">{errors.year}</div>
            )}
          </div>

          {/* Status */}
          <div className="form-group">
            <label className="form-label required_field_label">Status</label>
            <CustomSelect
              label="Status"
              options={
                TruckStatus
                  ? Object.values(TruckStatus).map((status) => ({
                      value: status,
                      label: status,
                    }))
                  : []
              }
              placeholder="Select status"
              onChange={(value) =>
                handleSelectChange("status", value as string)
              }
              error={!!errors.status}
            />
            {errors.status && (
              <div className="form-help error">{errors.status}</div>
            )}
          </div>

          {/* Capacity */}
          <div className="form-group">
            <label className="form-label required_field_label">Capacity</label>
            <input
              type="number"
              name="capacity"
              value={formData.capacity}
              onChange={handleInputChange}
              min="1"
              className={`form-input ${errors.capacity ? "error" : ""}`}
            />
            {errors.capacity && (
              <div className="form-help error">{errors.capacity}</div>
            )}
          </div>

          {/* Capacity Unit */}
          <div className="form-group">
            <label className="form-label required_field_label">
              Capacity Unit
            </label>
            <CustomSelect
              label="Capacity Unit"
              options={[
                { value: "kg", label: "kg" },
                { value: "tons", label: "tons" },
                { value: "lbs", label: "lbs" },
              ]}
              placeholder="Select capacity unit"
              onChange={(value) =>
                handleSelectChange("capacityUnit", value as string)
              }
              error={!!errors.capacityUnit}
            />
            {errors.capacityUnit && (
              <div className="form-help error">{errors.capacityUnit}</div>
            )}
          </div>

          {/* Fuel Type */}
          <div className="form-group">
            <label className="form-label required_field_label">Fuel Type</label>
            <input
              type="text"
              name="fuelType"
              value={formData.fuelType}
              onChange={handleInputChange}
              placeholder="Enter fuel type"
              className={`form-input ${errors.fuelType ? "error" : ""}`}
            />
            {errors.fuelType && (
              <div className="form-help error">{errors.fuelType}</div>
            )}
          </div>

          {/* Trailer Type */}
          <div className="form-group">
            <label className="form-label required_field_label">
              Trailer Type
            </label>
            <CustomSelect
              label="Trailer Type"
              options={
                TrailerType
                  ? Object.values(TrailerType).map((type) => ({
                      value: type,
                      label: type.replace("_", " "),
                    }))
                  : []
              }
              placeholder="Select trailer type"
              onChange={(value) =>
                handleSelectChange("trailerType", value as string)
              }
              error={!!errors.trailerType}
            />
            {errors.trailerType && (
              <div className="form-help error">{errors.trailerType}</div>
            )}
          </div>

          {/* Container Type */}
          <div className="form-group">
            <label className="form-label required_field_label">
              Container Type
            </label>
            <CustomSelect
              label="Container Type"
              options={
                ContainerType
                  ? Object.values(ContainerType).map((type) => ({
                      value: type,
                      label: type,
                    }))
                  : []
              }
              placeholder="Select container type"
              onChange={(value) =>
                handleSelectChange("containerType", value as string)
              }
              error={!!errors.containerType}
            />
            {errors.containerType && (
              <div className="form-help error">{errors.containerType}</div>
            )}
          </div>

          {/* Insurance Expiry */}
          <div className="form-group">
            <label className="form-label required_field_label">
              Insurance Expiry
            </label>
            <input
              type="date"
              name="insuranceExpiryDate"
              value={formatDateForInput(formData.insuranceExpiryDate)}
              onChange={(e) =>
                handleDateChange("insuranceExpiryDate", e.target.value)
              }
              className={`form-input ${
                errors.insuranceExpiryDate ? "error" : ""
              }`}
            />
            {errors.insuranceExpiryDate && (
              <div className="form-help error">
                {errors.insuranceExpiryDate}
              </div>
            )}
          </div>

          {/* Permit Expiry */}
          <div className="form-group">
            <label className="form-label required_field_label">
              Permit Expiry
            </label>
            <input
              type="date"
              name="permitExpiryDate"
              value={formatDateForInput(formData.permitExpiryDate)}
              onChange={(e) =>
                handleDateChange("permitExpiryDate", e.target.value)
              }
              className={`form-input ${errors.permitExpiryDate ? "error" : ""}`}
            />
            {errors.permitExpiryDate && (
              <div className="form-help error">{errors.permitExpiryDate}</div>
            )}
          </div>

          {/* Fitness Expiry */}
          <div className="form-group">
            <label className="form-label required_field_label">
              Fitness Expiry
            </label>
            <input
              type="date"
              name="fitnessExpiryDate"
              value={formatDateForInput(formData.fitnessExpiryDate)}
              onChange={(e) =>
                handleDateChange("fitnessExpiryDate", e.target.value)
              }
              className={`form-input ${
                errors.fitnessExpiryDate ? "error" : ""
              }`}
            />
            {errors.fitnessExpiryDate && (
              <div className="form-help error">{errors.fitnessExpiryDate}</div>
            )}
          </div>

          {/* PUC Expiry */}
          <div className="form-group">
            <label className="form-label required_field_label">
              PUC Expiry
            </label>
            <input
              type="date"
              name="pucExpiryDate"
              value={formatDateForInput(formData.pucExpiryDate)}
              onChange={(e) =>
                handleDateChange("pucExpiryDate", e.target.value)
              }
              className={`form-input ${errors.pucExpiryDate ? "error" : ""}`}
            />
            {errors.pucExpiryDate && (
              <div className="form-help error">{errors.pucExpiryDate}</div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TruckForm;
